/**
 * График динамики просмотров по снапшотам (ферма / телефон / модель / акк).
 * Одна линия — просмотры за 24ч по дням: ось всегда в одном масштабе,
 * без второй оси «всего просмотров», которая ломала читабельность.
 */
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis, type TooltipProps,
} from 'recharts';
import { formatNum, formatViews } from '../lib/format.js';

export interface SnapshotChartPoint {
  date: string;
  views24h: number;
}

interface Props {
  points: SnapshotChartPoint[];
  height?: number;
}

function fmtDate(d: string): string {
  const dt = new Date(d);
  return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

/** Точка = отчётный день 10:00→10:00 (Варшава): подписываем парой дат. */
function fmtReportDay(d: string): string {
  const dt = new Date(d);
  const next = new Date(dt);
  next.setDate(next.getDate() + 1);
  return `${fmtDate(d)}–${next.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`;
}

function ChartTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="tooltip-card" style={{ minWidth: 170 }}>
      <div className="tooltip-name">{fmtReportDay(String(label))} · с 10 до 10 утра</div>
      <div className="tooltip-grid">
        <span>Просмотры</span>
        <b>{formatNum(Number(payload[0]?.value ?? 0))}</b>
      </div>
    </div>
  );
}

export function SnapshotChart({ points, height = 320 }: Props) {
  if (points.length === 0) {
    return <div className="empty-state">Пока нет данных для графика — снапшоты появятся при обновлении метрик.</div>;
  }
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={points} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtReportDay}
            stroke="var(--text-muted)"
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={formatViews}
            stroke="var(--text-muted)"
            tick={{ fontSize: 11 }}
            width={52}
          />
          <Tooltip content={<ChartTooltip />} />
          <Line
            type="monotone"
            dataKey="views24h"
            name="Просмотры 24ч"
            stroke="var(--accent)"
            strokeWidth={2}
            dot={{ r: 3.5 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
